import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    IconButton,
    TextField,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Collapse,
    ToggleButtonGroup,
    ToggleButton,
    Divider,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
    createTheme,
    updateTheme,
    deleteTheme,
    createSubTheme,
    updateSubTheme,
    deleteSubTheme,
    getGlobalConfig,
} from '../../services/api';

/**
 * ThemeEditor Component
 * 
 * Provides CRUD operations for themes, sub-themes, and identification rules
 */
function ThemeEditor({ legacyThemes, catalystThemes, onUpdate, showNotification }) {
    const [system, setSystem] = useState('legacy');
    const [themes, setThemes] = useState({});
    const [expandedThemes, setExpandedThemes] = useState({});
    const [editingTheme, setEditingTheme] = useState(null);
    const [newThemeDialog, setNewThemeDialog] = useState(false);
    const [newSubThemeDialog, setNewSubThemeDialog] = useState({ open: false, themeName: null });
    const [newRuleDialog, setNewRuleDialog] = useState({ open: false, themeName: null });
    const [newTheme, setNewTheme] = useState({ name: '', description: '', keywords: '' });
    const [newSubTheme, setNewSubTheme] = useState({ name: '', keywords: '' });
    const [newRule, setNewRule] = useState({ type: 'report_name_contains', values: '' });
    const [saving, setSaving] = useState(false);
    const [newKeyword, setNewKeyword] = useState('');
    const [subThemeKeywords, setSubThemeKeywords] = useState({});

    // Rule types
    const ruleTypes = [
        { value: 'report_name_contains', label: 'Report Name Contains' },
        { value: 'filter_condition', label: 'Filter Condition' },
    ];

    // Update themes when system changes
    useEffect(() => {
        setThemes(system === 'legacy' ? legacyThemes : catalystThemes);
        setExpandedThemes({});
        setEditingTheme(null);
        setSubThemeKeywords({});
    }, [system, legacyThemes, catalystThemes]);

    const handleSystemChange = (event, newSystem) => {
        if (newSystem) {
            setSystem(newSystem);
        }
    };

    const toggleExpand = (themeName) => {
        setExpandedThemes(prev => ({
            ...prev,
            [themeName]: !prev[themeName]
        }));
    };

    const handleCreateTheme = async () => {
        if (!newTheme.name.trim()) {
            showNotification('Theme name is required', 'error');
            return;
        }

        try {
            setSaving(true);
            const themeData = {
                description: newTheme.description,
                keywords: newTheme.keywords.split('\n').filter(k => k.trim()),
            };
            await createTheme(system, newTheme.name.toLowerCase().replace(/\s+/g, '_'), themeData);
            const config = await getGlobalConfig();
            onUpdate(config);
            setNewThemeDialog(false);
            setNewTheme({ name: '', description: '', keywords: '' });
            showNotification(`Theme "${newTheme.name}" created successfully`);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to create theme', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTheme = async (themeName) => {
        if (!window.confirm(`Are you sure you want to delete the theme "${themeName}"?`)) {
            return;
        }

        try {
            setSaving(true);
            await deleteTheme(system, themeName);
            const config = await getGlobalConfig();
            onUpdate(config);
            showNotification(`Theme "${themeName}" deleted successfully`);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to delete theme', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateTheme = async (themeName, themeData) => {
        try {
            setSaving(true);
            await updateTheme(system, themeName, themeData);
            const config = await getGlobalConfig();
            onUpdate(config);
            setEditingTheme(null);
            showNotification(`Theme "${themeName}" updated successfully`);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to update theme', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleAddKeyword = async (themeName, keyword) => {
        if (!keyword.trim()) return;

        const theme = themes[themeName];
        const newKeywords = [...(theme.keywords || []), keyword.trim()];
        await handleUpdateTheme(themeName, { keywords: newKeywords });
        setNewKeyword('');
    };

    const handleRemoveKeyword = async (themeName, keywordToRemove) => {
        const theme = themes[themeName];
        const newKeywords = (theme.keywords || []).filter(k => k !== keywordToRemove);
        await handleUpdateTheme(themeName, { keywords: newKeywords });
    };

    // Sub-theme operations
    const handleCreateSubTheme = async () => {
        if (!newSubTheme.name.trim()) {
            showNotification('Sub-theme name is required', 'error');
            return;
        }

        try {
            setSaving(true);
            const keywords = newSubTheme.keywords.split('\n').filter(k => k.trim());
            await createSubTheme(
                system,
                newSubThemeDialog.themeName,
                newSubTheme.name.toLowerCase().replace(/\s+/g, '_'),
                keywords
            );
            const config = await getGlobalConfig();
            onUpdate(config);
            setNewSubThemeDialog({ open: false, themeName: null });
            setNewSubTheme({ name: '', keywords: '' });
            showNotification(`Sub-theme "${newSubTheme.name}" created successfully`);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to create sub-theme', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteSubTheme = async (themeName, subThemeName) => {
        if (!window.confirm(`Are you sure you want to delete the sub-theme "${subThemeName}"?`)) {
            return;
        }

        try {
            setSaving(true);
            await deleteSubTheme(system, themeName, subThemeName);
            const config = await getGlobalConfig();
            onUpdate(config);
            showNotification(`Sub-theme "${subThemeName}" deleted successfully`);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to delete sub-theme', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleAddSubThemeKeyword = async (themeName, subThemeName) => {
        const key = `${themeName}-${subThemeName}`;
        const keyword = subThemeKeywords[key];
        if (!keyword?.trim()) return;

        const theme = themes[themeName];
        const subTheme = theme.sub_themes?.[subThemeName];
        const newKeywords = [...(subTheme?.keywords || []), keyword.trim()];

        try {
            setSaving(true);
            await updateSubTheme(system, themeName, subThemeName, newKeywords);
            const config = await getGlobalConfig();
            onUpdate(config);
            setSubThemeKeywords(prev => ({ ...prev, [key]: '' }));
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to add keyword', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveSubThemeKeyword = async (themeName, subThemeName, keywordToRemove) => {
        const theme = themes[themeName];
        const subTheme = theme.sub_themes?.[subThemeName];
        const newKeywords = (subTheme?.keywords || []).filter(k => k !== keywordToRemove);

        try {
            setSaving(true);
            await updateSubTheme(system, themeName, subThemeName, newKeywords);
            const config = await getGlobalConfig();
            onUpdate(config);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to remove keyword', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Identification Rules operations
    const handleAddRule = async () => {
        if (!newRule.values.trim()) {
            showNotification('Rule values are required', 'error');
            return;
        }

        try {
            setSaving(true);
            const theme = themes[newRuleDialog.themeName];
            const existingRules = theme.identification_rules || [];
            const values = newRule.values.split('\n').filter(v => v.trim());
            const updatedRules = [...existingRules, { type: newRule.type, values }];

            await updateTheme(system, newRuleDialog.themeName, { identification_rules: updatedRules });
            const config = await getGlobalConfig();
            onUpdate(config);
            setNewRuleDialog({ open: false, themeName: null });
            setNewRule({ type: 'report_name_contains', values: '' });
            showNotification('Identification rule added successfully');
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to add rule', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteRule = async (themeName, ruleIndex) => {
        if (!window.confirm('Are you sure you want to delete this identification rule?')) {
            return;
        }

        try {
            setSaving(true);
            const theme = themes[themeName];
            const updatedRules = [...(theme.identification_rules || [])];
            updatedRules.splice(ruleIndex, 1);

            await updateTheme(system, themeName, {
                identification_rules: updatedRules.length > 0 ? updatedRules : null
            });
            const config = await getGlobalConfig();
            onUpdate(config);
            showNotification('Identification rule deleted');
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to delete rule', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleAddRuleValue = async (themeName, ruleIndex, value) => {
        if (!value.trim()) return;

        try {
            setSaving(true);
            const theme = themes[themeName];
            const updatedRules = [...(theme.identification_rules || [])];
            updatedRules[ruleIndex] = {
                ...updatedRules[ruleIndex],
                values: [...(updatedRules[ruleIndex].values || []), value.trim()]
            };

            await updateTheme(system, themeName, { identification_rules: updatedRules });
            const config = await getGlobalConfig();
            onUpdate(config);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to add rule value', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveRuleValue = async (themeName, ruleIndex, valueToRemove) => {
        try {
            setSaving(true);
            const theme = themes[themeName];
            const updatedRules = [...(theme.identification_rules || [])];
            updatedRules[ruleIndex] = {
                ...updatedRules[ruleIndex],
                values: (updatedRules[ruleIndex].values || []).filter(v => v !== valueToRemove)
            };

            await updateTheme(system, themeName, { identification_rules: updatedRules });
            const config = await getGlobalConfig();
            onUpdate(config);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to remove rule value', 'error');
        } finally {
            setSaving(false);
        }
    };

    const getKeywordCount = (theme) => {
        let count = (theme.keywords || []).length;
        if (theme.sub_themes) {
            Object.values(theme.sub_themes).forEach(sub => {
                count += (sub.keywords || []).length;
            });
        }
        return count;
    };

    return (
        <Box>
            {/* System Toggle */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <ToggleButtonGroup
                    value={system}
                    exclusive
                    onChange={handleSystemChange}
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            px: 3,
                            py: 1,
                        }
                    }}
                >
                    <ToggleButton value="legacy" sx={{ textTransform: 'none' }}>
                        Legacy ({Object.keys(legacyThemes || {}).length})
                    </ToggleButton>
                    <ToggleButton value="catalyst" sx={{ textTransform: 'none' }}>
                        Catalyst ({Object.keys(catalystThemes || {}).length})
                    </ToggleButton>
                </ToggleButtonGroup>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setNewThemeDialog(true)}
                >
                    Add Theme
                </Button>
            </Box>

            {/* Themes Grid */}
            <Grid container spacing={2}>
                {Object.entries(themes || {}).map(([themeName, theme]) => (
                    <Grid item xs={12} md={6} key={themeName}>
                        <Card sx={{
                            transition: 'all 0.2s',
                            '&:hover': {
                                boxShadow: '0 4px 16px rgba(0, 119, 200, 0.15)',
                            }
                        }}>
                            <CardContent sx={{ pb: 1 }}>
                                {/* Theme Header */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" fontWeight={600} color="primary.main">
                                            {themeName.replace(/_/g, ' ')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {theme.description}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <IconButton size="small" onClick={() => toggleExpand(themeName)}>
                                            {expandedThemes[themeName] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteTheme(themeName)}
                                            sx={{ '&:hover': { color: 'error.main' } }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>

                                {/* Stats */}
                                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip label={`${getKeywordCount(theme)} keywords`} size="small" variant="outlined" />
                                    {theme.sub_themes && (
                                        <Chip
                                            label={`${Object.keys(theme.sub_themes).length} sub-themes`}
                                            size="small" variant="outlined" color="secondary"
                                        />
                                    )}
                                    {theme.identification_rules && (
                                        <Chip
                                            label={`${theme.identification_rules.length} rules`}
                                            size="small" variant="outlined" color="info"
                                        />
                                    )}
                                </Box>

                                {/* Expanded Content */}
                                <Collapse in={expandedThemes[themeName]}>
                                    <Box sx={{ mt: 2 }}>
                                        {/* Keywords Section */}
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                            Keywords
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                            {(theme.keywords || []).map((keyword, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={keyword}
                                                    size="small"
                                                    onDelete={() => handleRemoveKeyword(themeName, keyword)}
                                                    sx={{ fontSize: '0.75rem' }}
                                                />
                                            ))}
                                            {(!theme.keywords || theme.keywords.length === 0) && (
                                                <Typography variant="caption" color="text.secondary">
                                                    No keywords defined
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Add Keyword Input */}
                                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                            <TextField
                                                size="small"
                                                placeholder="Add keyword..."
                                                value={editingTheme === themeName ? newKeyword : ''}
                                                onChange={(e) => {
                                                    setEditingTheme(themeName);
                                                    setNewKeyword(e.target.value);
                                                }}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleAddKeyword(themeName, newKeyword);
                                                    }
                                                }}
                                                sx={{ flex: 1 }}
                                            />
                                            <Button
                                                size="small" variant="outlined"
                                                onClick={() => handleAddKeyword(themeName, newKeyword)}
                                                disabled={!newKeyword.trim()}
                                            >
                                                Add
                                            </Button>
                                        </Box>

                                        {/* Sub-themes Section */}
                                        {theme.sub_themes && (
                                            <>
                                                <Divider sx={{ my: 2 }} />
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Sub-themes
                                                    </Typography>
                                                    <Button
                                                        size="small" startIcon={<AddIcon />}
                                                        onClick={() => setNewSubThemeDialog({ open: true, themeName })}
                                                    >
                                                        Add
                                                    </Button>
                                                </Box>

                                                {Object.entries(theme.sub_themes).map(([subName, subTheme]) => {
                                                    const inputKey = `${themeName}-${subName}`;
                                                    return (
                                                        <Paper key={subName} sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50' }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                <Typography variant="body2" fontWeight={500}>
                                                                    {subName.replace(/_/g, ' ')}
                                                                </Typography>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteSubTheme(themeName, subName)}
                                                                    sx={{ '&:hover': { color: 'error.main' } }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                                                {(subTheme.keywords || []).map((keyword, idx) => (
                                                                    <Chip
                                                                        key={idx}
                                                                        label={keyword}
                                                                        size="small"
                                                                        onDelete={() => handleRemoveSubThemeKeyword(themeName, subName, keyword)}
                                                                        sx={{ fontSize: '0.7rem' }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                            {/* Add keyword to sub-theme */}
                                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                                <TextField
                                                                    size="small"
                                                                    placeholder="Add keyword..."
                                                                    value={subThemeKeywords[inputKey] || ''}
                                                                    onChange={(e) => setSubThemeKeywords(prev => ({
                                                                        ...prev,
                                                                        [inputKey]: e.target.value
                                                                    }))}
                                                                    onKeyPress={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            handleAddSubThemeKeyword(themeName, subName);
                                                                        }
                                                                    }}
                                                                    sx={{ flex: 1, bgcolor: 'white' }}
                                                                />
                                                                <Button
                                                                    size="small" variant="outlined"
                                                                    onClick={() => handleAddSubThemeKeyword(themeName, subName)}
                                                                    disabled={!subThemeKeywords[inputKey]?.trim()}
                                                                >
                                                                    Add
                                                                </Button>
                                                            </Box>
                                                        </Paper>
                                                    );
                                                })}
                                            </>
                                        )}

                                        {/* Add Sub-theme Button (if no sub-themes exist) */}
                                        {!theme.sub_themes && (
                                            <Button
                                                size="small" variant="text" startIcon={<AddIcon />}
                                                onClick={() => setNewSubThemeDialog({ open: true, themeName })}
                                                sx={{ mt: 1 }}
                                            >
                                                Add Sub-theme
                                            </Button>
                                        )}

                                        {/* Identification Rules Section */}
                                        <Divider sx={{ my: 2 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Identification Rules
                                            </Typography>
                                            <Button
                                                size="small" startIcon={<AddIcon />}
                                                onClick={() => setNewRuleDialog({ open: true, themeName })}
                                            >
                                                Add Rule
                                            </Button>
                                        </Box>

                                        {theme.identification_rules && theme.identification_rules.length > 0 ? (
                                            theme.identification_rules.map((rule, ruleIdx) => (
                                                <Paper key={ruleIdx} sx={{ p: 1.5, mb: 1, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="caption" color="info.dark" fontWeight={600}>
                                                            {rule.type.replace(/_/g, ' ')}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteRule(themeName, ruleIdx)}
                                                            sx={{ '&:hover': { color: 'error.main' } }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                                        {(rule.values || []).map((val, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={val}
                                                                size="small"
                                                                onDelete={() => handleRemoveRuleValue(themeName, ruleIdx, val)}
                                                                sx={{ fontSize: '0.7rem' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                    {/* Add value to rule */}
                                                    <RuleValueInput
                                                        onAdd={(value) => handleAddRuleValue(themeName, ruleIdx, value)}
                                                    />
                                                </Paper>
                                            ))
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                No identification rules defined
                                            </Typography>
                                        )}
                                    </Box>
                                </Collapse>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Empty State */}
            {Object.keys(themes || {}).length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No themes defined for {system}
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewThemeDialog(true)}>
                        Add First Theme
                    </Button>
                </Box>
            )}

            {/* New Theme Dialog */}
            <Dialog open={newThemeDialog} onClose={() => setNewThemeDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add New Theme ({system})</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus margin="dense" label="Theme Name" fullWidth
                        value={newTheme.name}
                        onChange={(e) => setNewTheme({ ...newTheme, name: e.target.value })}
                        placeholder="e.g., payments" sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense" label="Description" fullWidth
                        value={newTheme.description}
                        onChange={(e) => setNewTheme({ ...newTheme, description: e.target.value })}
                        placeholder="Reports related to..." sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense" label="Keywords (one per line)" fullWidth multiline rows={4}
                        value={newTheme.keywords}
                        onChange={(e) => setNewTheme({ ...newTheme, keywords: e.target.value })}
                        placeholder="KEYWORD_1&#10;KEYWORD_2&#10;KEYWORD_3"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setNewThemeDialog(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleCreateTheme} variant="contained" disabled={!newTheme.name.trim() || saving}>
                        {saving ? 'Creating...' : 'Create Theme'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* New Sub-Theme Dialog */}
            <Dialog
                open={newSubThemeDialog.open}
                onClose={() => setNewSubThemeDialog({ open: false, themeName: null })}
                maxWidth="sm" fullWidth
            >
                <DialogTitle>Add Sub-Theme to {newSubThemeDialog.themeName}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus margin="dense" label="Sub-Theme Name" fullWidth
                        value={newSubTheme.name}
                        onChange={(e) => setNewSubTheme({ ...newSubTheme, name: e.target.value })}
                        placeholder="e.g., status_detail" sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense" label="Keywords (one per line)" fullWidth multiline rows={4}
                        value={newSubTheme.keywords}
                        onChange={(e) => setNewSubTheme({ ...newSubTheme, keywords: e.target.value })}
                        placeholder="KEYWORD_1&#10;KEYWORD_2&#10;KEYWORD_3"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setNewSubThemeDialog({ open: false, themeName: null })} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleCreateSubTheme} variant="contained" disabled={!newSubTheme.name.trim() || saving}>
                        {saving ? 'Creating...' : 'Create Sub-Theme'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* New Identification Rule Dialog */}
            <Dialog
                open={newRuleDialog.open}
                onClose={() => setNewRuleDialog({ open: false, themeName: null })}
                maxWidth="sm" fullWidth
            >
                <DialogTitle>Add Identification Rule to {newRuleDialog.themeName}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                        <InputLabel>Rule Type</InputLabel>
                        <Select
                            value={newRule.type}
                            label="Rule Type"
                            onChange={(e) => setNewRule({ ...newRule, type: e.target.value })}
                        >
                            {ruleTypes.map(rt => (
                                <MenuItem key={rt.value} value={rt.value}>{rt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense" label="Values (one per line)" fullWidth multiline rows={4}
                        value={newRule.values}
                        onChange={(e) => setNewRule({ ...newRule, values: e.target.value })}
                        placeholder="scorecard&#10;report_name"
                        helperText={newRule.type === 'filter_condition'
                            ? 'Enter filter conditions like: [Claim Type Code] = \'S\''
                            : 'Enter keywords to match in report names'}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setNewRuleDialog({ open: false, themeName: null })} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleAddRule} variant="contained" disabled={!newRule.values.trim() || saving}>
                        {saving ? 'Adding...' : 'Add Rule'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// Helper component for adding values to rules
function RuleValueInput({ onAdd }) {
    const [value, setValue] = useState('');

    const handleAdd = () => {
        if (value.trim()) {
            onAdd(value.trim());
            setValue('');
        }
    };

    return (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <TextField
                size="small"
                placeholder="Add value..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyPress={(e) => {
                    if (e.key === 'Enter') handleAdd();
                }}
                sx={{ flex: 1, bgcolor: 'white' }}
            />
            <Button size="small" variant="outlined" onClick={handleAdd} disabled={!value.trim()}>
                Add
            </Button>
        </Box>
    );
}

export default ThemeEditor;
